import React, { FC, useCallback, useMemo, useState } from 'react';
import { faRedo } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Country, Role } from '@tendium/prom-types';
import generator from 'generate-password';
import { useTranslation } from 'react-i18next';
import { Button, Form, Input, Modal, Select } from 'src/common';
import { PASSWORD_TO_GENERATE_POLICY } from 'src/config/config';
import { useAvailableRoles, useDefaultBasicRoles } from 'src/models/users/Roles/hooks';
import CreateResult from '../CreateUserResult';
import styles from './index.module.scss';
import { useCreateUser } from 'src/models/admin/hooks';

export interface IUserAddModalProps {
  onHide: () => void;
  refetch?: () => void;
  companyId?: string;
}

const FormLayout = { labelCol: { span: 8 }, wrapperCol: { span: 16 } };
const generatePassword = (): string => generator.generate(PASSWORD_TO_GENERATE_POLICY);
interface FormState {
  name: string;
  lastName: string;
  email: string;
  password: string;
  roles: Role[];
  country: Country;
}
export const AddUserModal: FC<IUserAddModalProps> = ({ onHide, companyId, refetch }) => {
  const { t } = useTranslation();

  const [, availablePaymentsRoles] = useAvailableRoles({ excludeNonActivated: true });
  const avialableSalesOffice = Object.keys(Country);

  const initialRoles = useDefaultBasicRoles();

  const initialValues = useMemo((): FormState => {
    return {
      name: '',
      lastName: '',
      email: '',
      password: '',
      roles: initialRoles,
      country: Country.SWEDEN
    };
  }, [initialRoles]);

  const [success, setSuccess] = useState(false);

  const [form] = Form.useForm<FormState>();
  const onGeneratePassword = useCallback(() => {
    const newValues: Partial<FormState> = {
      password: generatePassword()
    };
    form.setFieldsValue(newValues);
  }, [form]);

  const [createUser, { loading }] = useCreateUser();

  const onFinish = useCallback(
    (values: FormState) => {
      const { name, lastName, email, roles, password, country } = values;
      createUser({
        name,
        lastName,
        email,
        roles: roles ?? initialRoles,
        password,
        companyId,
        country,
        onComplete: () => {
          setSuccess(true);
          refetch?.();
        }
      });
    },
    [companyId, createUser, initialRoles, refetch]
  );

  return (
    <Modal
      title={t('AdminPage.createUser')}
      visible
      onCancel={onHide}
      footer={
        <>
          <Button type={'text'} onClick={onHide} data-action={'close'}>
            {t('Common.cancel')}
          </Button>

          {!success && (
            <Button
              type={'primary'}
              htmlType={'submit'}
              disabled={loading}
              data-action={'create'}
              onClick={form.submit}
              loading={!!loading}
            >
              {t('Common.create')}
            </Button>
          )}
        </>
      }
    >
      {success && <CreateResult email={form.getFieldValue('email')} password={form.getFieldValue('password')} />}
      {!success && (
        <Form {...FormLayout} form={form} initialValues={initialValues} onFinish={onFinish}>
          <Form.Item label={t('Account.firstName')} name={'name'} rules={[{ required: true }]}>
            <Input placeholder={t('Account.firstName')} autoComplete={'given-name'} />
          </Form.Item>
          <Form.Item label={t('Account.lastName')} name={'lastName'} rules={[{ required: true }]}>
            <Input placeholder={t('Account.lastName')} autoComplete={'family-name'} />
          </Form.Item>
          <Form.Item
            label={t('Auth.emailAddress')}
            name={'email'}
            rules={[{ required: true }, { type: 'email' }]}
            validateTrigger={false}
          >
            <Input type="email" placeholder={t('Auth.emailAddress')} autoComplete={'email'} />
          </Form.Item>
          <Form.Item label={t('Common.password')} name={'password'} rules={[{ required: true }]}>
            <Input.Password
              placeholder={t('Common.password')}
              autoComplete={'new-password'}
              addonAfter={
                <Button
                  type={'link'}
                  onClick={onGeneratePassword}
                  icon={<FontAwesomeIcon icon={faRedo} className={styles.icon} />}
                />
              }
            />
          </Form.Item>
          {!companyId && (
            <Form.Item label={t('AdminPage.pricePlan')} name={'roles'} rules={[{ required: true }]}>
              <Select listHeight={512}>
                {availablePaymentsRoles.map(val => (
                  <Select.Option value={val} key={val}>
                    {t(`FeatureRole.role_${val}`)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          {!companyId && (
            <Form.Item label={t('AdminPage.salesOffice')} name={'country'} rules={[{ required: true }]}>
              <Select listHeight={512}>
                {avialableSalesOffice.map(val => (
                  <Select.Option value={val} key={val}>
                    {t(`SalesOffice.${val}`)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </Form>
      )}
    </Modal>
  );
};

export default AddUserModal;
